import { Formik } from 'formik';
import { useDispatch } from 'react-redux';

import FormTitle from 'components/PageLayout/FormTitle';
import PhoneNumberInput from 'components/curo-components/common/PhoneNumberInput';
import ButtonWithLoadingState from 'components/digifi-wrappers/ButtonWithLoadingState';
import { getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { setPhone } from 'handlers/curo-handlers/signInProcessSlice';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import CuroMainLayout from 'layout/CuroMainLayout';
import { sendVerificationCode } from 'thunks';
import { createFormikHelpers } from 'utils/curo-helpers/formHelpers';

import styles from './SIgnInProcessStep.module.scss';
import {
  IPhoneNumberFormValues,
  PhoneNumberFormFields,
  phoneNumberFormValidationSchema,
} from './phoneNumberStepValidation';

const PAGE_TITLE = 'Sign In';
const PAGE_DESCRIPTION = 'Please enter your cell phone number.';

interface IPhoneNumberStepProps {
  onStepComplete: () => void;
}

const PhoneNumberStep = ({ onStepComplete }: IPhoneNumberStepProps) => {
  const { phone } = useAppSelector(getSignInProcessState);
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const handleSubmit = async (values: IPhoneNumberFormValues) => {
    try {
      await dispatch(setPhone({ phone: values[PhoneNumberFormFields.Phone] }));
      await dispatchWithUnwrap(sendVerificationCode(values[PhoneNumberFormFields.Phone]));
      onStepComplete();
    } catch {
      // CURO-TODO add error handling
    }
  };

  return (
    <CuroMainLayout>
      <FormTitle title={PAGE_TITLE} subTitle={PAGE_DESCRIPTION} />
      <Formik
        initialValues={{ phone }}
        validationSchema={phoneNumberFormValidationSchema}
        validateOnBlur
        validateOnChange
        validateOnMount
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ touched, errors, submitForm, isValid, setFieldValue, setTouched }) => {
          const handleContinue = async () => {
            await submitForm();
          };

          const { handleInputChange, handleTouched, getInputErrorMessage } = createFormikHelpers<
            PhoneNumberFormFields,
            IPhoneNumberFormValues
          >(setFieldValue, setTouched, touched, errors);

          return (
            <section className={styles.contentWrapper}>
              <PhoneNumberInput
                labelTitle="Cell Phone Number"
                value={phone}
                onChange={handleInputChange(PhoneNumberFormFields.Phone)}
                onBlur={handleTouched(PhoneNumberFormFields.Phone)}
                errorMessage={getInputErrorMessage(PhoneNumberFormFields.Phone)}
                required
              />
              <ButtonWithLoadingState
                onClick={handleContinue}
                kind="primary"
                size="form"
                disabled={!isValid}
              >
                Continue
              </ButtonWithLoadingState>
            </section>
          );
        }}
      </Formik>
    </CuroMainLayout>
  );
};

export default PhoneNumberStep;
